import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Stack, Chip, Grid, Button, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  FormControl, InputLabel, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import facturaVentaService from '../../services/facturaVentaService';
import ubicacionService from '../../services/ubicacionService';
import pedidoVentaService from '../../services/pedidoVentaService';
import clienteService from '../../services/clienteService';
import { transformarPedidoAFactura } from '../../utils/pedidoToFactura';
import { obtenerCodigoListaCliente } from '../../utils/precioListaPedido';
import { parseLocalDate, toLocalDateStringYMD } from '../../utils/formatters';
import FacturaPOSClientePanel from './pos/FacturaPOSClientePanel';
import FacturaPOSSearchBar, { SuggestionItem } from './pos/FacturaPOSSearchBar';
import FacturaPOSLineasGrid from './pos/FacturaPOSLineasGrid';
import FacturaPOSTotalesPanel from './pos/FacturaPOSTotalesPanel';
import { POSCliente, POSLinea } from './pos/types';

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const calcularSubtotal = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number => {
  const base = l.cantidad * l.precioUnitario;
  return Math.round(base * (1 - l.descuento / 100) * 100) / 100;
};

const IVA_ALICUOTA = 0.21;

interface Sucursal { nroSuc: number; nombreSucursal?: string; }
interface Compania { idCompania: string; nombre: string; }

const extractApiError = (e: any): string => {
  const data = e?.response?.data;
  if (!data) return e?.message ?? 'Error al guardar la factura';
  if (typeof data === 'string') return data;
  if (data.message) return data.message;
  if (data.title) return data.title;
  if (data.errors) return Object.values(data.errors).flat().join(' | ');
  return JSON.stringify(data).substring(0, 300);
};

const NuevaFacturaPOS: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const nroFormularioParam = searchParams.get('nroFormulario');
  const nroVer = nroFormularioParam ? parseInt(nroFormularioParam, 10) : NaN;
  const modoVerPos = Number.isFinite(nroVer) && nroVer > 0;

  const pedidoIdParam = searchParams.get('pedidoId');
  const pedidoIdNum = pedidoIdParam ? parseInt(pedidoIdParam, 10) : NaN;
  const modoDesdePedido = Number.isFinite(pedidoIdNum) && pedidoIdNum > 0 && !modoVerPos;

  const [loadingFactura, setLoadingFactura] = useState(modoVerPos);
  const [loadingPedido, setLoadingPedido] = useState(modoDesdePedido);
  const [tituloPos, setTituloPos] = useState('');
  const [nroPedidoOrigen, setNroPedidoOrigen] = useState<number | null>(null);

  // ── Sucursal / Compañía ──────────────────────────────────────
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [companias, setCompanias] = useState<Compania[]>([]);
  const [nroSuc, setNroSuc] = useState<number>(0);
  const [idCompania, setIdCompania] = useState<string>('');
  const [loadingSuc, setLoadingSuc] = useState(false);

  useEffect(() => {
    const cargar = async () => {
      setLoadingSuc(true);
      try {
        const [resSuc, resCom] = await Promise.all([
          api.get('/api/SucursalesVenta'),
          api.get('/api/Compañias'),
        ]);
        const sucData: Sucursal[] = Array.isArray(resSuc.data)
          ? resSuc.data
          : resSuc.data?.items ?? [];
        const comData: Compania[] = Array.isArray(resCom.data)
          ? resCom.data
          : resCom.data?.items ?? [];

        setSucursales(sucData);
        setCompanias(comData);

        // Auto-seleccionar: usar última usada de localStorage, o la primera
        const stored = Number(localStorage.getItem('nroSuc') || '0');
        const primera = sucData.find((s) => s.nroSuc === stored) ?? sucData[0];
        if (primera) setNroSuc(primera.nroSuc);

        if (comData.length > 0) setIdCompania(comData[0].idCompania);
      } catch {
        /* sucursales no disponibles */
      } finally {
        setLoadingSuc(false);
      }
    };
    cargar();
  }, []);

  // Persistir nroSuc en localStorage cuando cambia
  useEffect(() => {
    if (nroSuc > 0) localStorage.setItem('nroSuc', String(nroSuc));
  }, [nroSuc]);

  useEffect(() => {
    if (modoVerPos || modoDesdePedido) return;
    setCliente(null);
    setLineas([]);
    setPorcDesc(0);
    setPorcDesc2(0);
    setPorcDesc3(0);
    setFormaDePago('');
    setOrdenCompra('');
    setTransporte('');
    setUbicacion('');
    setFacturaEmitida(null);
    setTituloPos('');
    setNroPedidoOrigen(null);
    setFechaEmision(toLocalDateStringYMD());
    setObservaciones('');
  }, [modoVerPos, modoDesdePedido]);

  useEffect(() => {
    if (!modoVerPos) return;
    let cancelled = false;
    (async () => {
      setLoadingFactura(true);
      try {
        const f: any = await facturaVentaService.getFacturaVenta(nroVer);
        if (cancelled) return;

        let ubicacionStr = f.ubicacion ?? '';
        if (!ubicacionStr && f.nroUbicacionOrigen) {
          try {
            const u = await ubicacionService.getUbicacion(f.nroUbicacionOrigen);
            if (u) ubicacionStr = `${u.nombreUbicacion ?? ''} (${u.nroUbicacion})`.trim();
            else ubicacionStr = `(${f.nroUbicacionOrigen})`;
          } catch {
            ubicacionStr = `(${f.nroUbicacionOrigen})`;
          }
        }

        const letra = (f.letra ?? '').toString().trim().toUpperCase();
        const ns = f.nroSuc;
        const nc = f.nroComprobante;
        const padNum = (value: unknown, len: number) => {
          const n = Number(value);
          if (Number.isFinite(n)) return String(n).padStart(len, '0');
          return String(value ?? '').padStart(len, '0');
        };
        if (letra && ns != null && nc != null) {
          setTituloPos(`${letra}-${padNum(ns, 5)}-${padNum(nc, 8)}`);
        } else {
          setTituloPos(`Factura #${f.nroFormulario ?? nroVer}`);
        }

        setNroSuc(f.nroSuc ?? 0);
        if (f.idCompania) setIdCompania(String(f.idCompania));
        setPorcDesc(Number(f.porcDesc ?? 0));
        setPorcDesc2(Number(f.porcDesc2 ?? 0));
        setPorcDesc3(Number(f.porcDesc3 ?? 0));
        setFormaDePago((f.formaDePago ?? '').trim());
        setOrdenCompra((f.ordenDeCompra ?? f.ordenCompra ?? '').trim());
        setTransporte((f.formaEntrega ?? f.transporte ?? '').trim());
        setUbicacion(ubicacionStr);
        if (f.fechaEmision) {
          const d = new Date(f.fechaEmision);
          setFechaEmision(!Number.isNaN(d.getTime()) ? toLocalDateStringYMD(d) : toLocalDateStringYMD());
        } else {
          setFechaEmision(toLocalDateStringYMD());
        }
        setObservaciones(String(f.descripcion ?? '').trim());

        setCliente({
          nroCliente: f.nroCliente ?? 0,
          nombre: (f.nombre ?? '').trim(),
          razonSocial: (f.nombre ?? '').trim(),
          cuit: f.cuit ?? undefined,
          tipoIva: (f.tipoIva ?? f.tipoCliente ?? '') || undefined,
          condVta: f.formaDePago ?? undefined,
          nroLista: f.nroLista ?? undefined,
          nroVendedor: f.nroVendedor ?? undefined,
          vendedorInfo:
            f.usuario || f.nroVendedor != null
              ? { nroVendedor: Number(f.nroVendedor ?? 0), nombreVendedor: f.usuario ?? undefined }
              : undefined,
        });

        const detalles = f.detalles ?? [];
        setLineas(
          detalles.map((d: any) => {
            const cantidad = Number(d.cantidad ?? 0);
            const precioUnitario = Number(d.precioUnitario ?? d.precioLista ?? 0);
            const descuento = Number(d.porcDesc ?? d.descuento ?? 0);
            const linea: POSLinea = {
              id: genId(),
              codigoArticulo: String(d.codigoArticulo ?? '').trim(),
              descripcionArticulo: (d.descripcionArticulo ?? '').trim(),
              cantidad,
              precioUnitario,
              descuento,
              subtotal: 0,
              um: (d.um ?? '').trim(),
              notas: d.notas,
            };
            linea.subtotal = calcularSubtotal(linea);
            return linea;
          }),
        );
      } catch (e: any) {
        if (!cancelled) {
          enqueueSnackbar(extractApiError(e) || 'Error al cargar la factura', { variant: 'error' });
          navigate('/ventas/facturas', { replace: true });
        }
      } finally {
        if (!cancelled) setLoadingFactura(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modoVerPos, nroVer, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (!modoDesdePedido) return;
    let cancelled = false;
    (async () => {
      setLoadingPedido(true);
      try {
        const pedido = await pedidoVentaService.getPedidoVenta(pedidoIdNum);
        const factura = await transformarPedidoAFactura(pedido);
        if (cancelled) return;

        setNroPedidoOrigen(pedidoIdNum);
        const ns = Number(factura.nroSuc ?? 0);
        if (ns > 0) setNroSuc(ns);
        if (factura.idCompania) setIdCompania(String(factura.idCompania));
        setPorcDesc(Number(factura.porcDesc ?? 0));
        setPorcDesc2(Number(factura.porcDesc2 ?? 0));
        setPorcDesc3(Number(factura.porcDesc3 ?? 0));
        const fpRaw = (factura.formaDePago ?? '').trim();
        if (fpRaw) {
          const fp = fpRaw.replace(' ', '.').toUpperCase();
          setFormaDePago(['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'CONTRAREEMBOLSO'].includes(fp) ? fp : fpRaw);
        }
        setOrdenCompra((factura as any).ordenCompra ?? (factura as any).ordenDeCompra ?? '');
        setTransporte((factura.transporte ?? '').trim());
        setUbicacion((factura.ubicacion ?? '').trim());
        const fe = (factura as any).fechaEmision;
        if (fe) {
          const d = new Date(fe);
          setFechaEmision(!Number.isNaN(d.getTime()) ? toLocalDateStringYMD(d) : toLocalDateStringYMD());
        } else {
          setFechaEmision(toLocalDateStringYMD());
        }
        setObservaciones(String((factura as any).descripcion ?? '').trim());

        const nroCli = Number(factura.nroCliente ?? 0);
        if (!Number.isFinite(nroCli) || nroCli <= 0) {
          throw new Error('El pedido no tiene un cliente válido');
        }

        try {
          const c = await clienteService.getCliente(nroCli);
          const ce = c as any;
          setCliente({
            nroCliente: c.nroCliente,
            nombre: c.nombre,
            razonSocial: c.nombre,
            cuit: c.cuit || factura.cuit,
            dni: ce.dni,
            tipoIva: c.tipoIva || (factura as any).tipoIVA,
            condVta: c.condVta || factura.formaDePago,
            nroLista: c.nroLista || factura.nroLista,
            nroVendedor: factura.nroVendedor || (Number(ce.vendedor) > 0 ? Number(ce.vendedor) : undefined),
            nroExpreso: factura.transporte ? Number(factura.transporte) : (ce.nroExpreso ?? undefined),
            porcDesc: c.porcDesc ?? factura.porcDesc ?? undefined,
            porcDesc2: c.porcDesc2 ?? factura.porcDesc2 ?? undefined,
            porcDesc3: c.porcDesc3 ?? factura.porcDesc3 ?? undefined,
            listaPrecio: ce.listaPrecio,
            vendedorInfo:
              pedido.usuario || factura.usuario
                ? { nroVendedor: Number(factura.nroVendedor ?? 0), nombreVendedor: pedido.usuario || factura.usuario }
                : undefined,
          });
        } catch {
          setCliente({
            nroCliente: nroCli,
            nombre: (factura.nombre ?? '').trim(),
            razonSocial: (factura.nombre ?? '').trim(),
            cuit: factura.cuit,
            tipoIva: (factura as any).tipoIVA,
            condVta: factura.formaDePago,
            nroLista: factura.nroLista,
            nroVendedor: factura.nroVendedor,
            nroExpreso: factura.transporte ? Number(factura.transporte) : undefined,
            porcDesc: factura.porcDesc ?? undefined,
            porcDesc2: factura.porcDesc2 ?? undefined,
            porcDesc3: factura.porcDesc3 ?? undefined,
            vendedorInfo:
              pedido.usuario || factura.usuario
                ? { nroVendedor: Number(factura.nroVendedor ?? 0), nombreVendedor: pedido.usuario || factura.usuario }
                : undefined,
          });
        }

        setLineas(
          (factura.detalles || []).map((d: any) => {
            const cantidad = Number(d.cantidad ?? 0);
            const precioLista = Number(d.precioUnitario ?? d.precioLista ?? 0);
            let desc = Number(d.porcDesc ?? d.descuento ?? 0);
            if (desc > 0 && desc <= 1) desc *= 100;
            desc = Math.max(0, Math.min(100, desc));
            const linea: POSLinea = {
              id: genId(),
              codigoArticulo: String(d.codigoArticulo ?? '').trim(),
              descripcionArticulo: String(d.descripcionArticulo ?? '').trim(),
              cantidad,
              precioUnitario: precioLista,
              descuento: desc,
              subtotal: 0,
              um: String(d.um ?? '').trim(),
              notas: d.notas,
            };
            linea.subtotal = calcularSubtotal(linea);
            return linea;
          }),
        );
      } catch (e: any) {
        if (!cancelled) {
          enqueueSnackbar(extractApiError(e) || 'Error al cargar el pedido', { variant: 'error' });
          navigate('/ventas/pedidos', { replace: true });
        }
      } finally {
        if (!cancelled) setLoadingPedido(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modoDesdePedido, pedidoIdNum, enqueueSnackbar, navigate]);

  // ── Estado de la factura ─────────────────────────────────────
  const [cliente, setCliente] = useState<POSCliente | null>(null);
  const [lineas, setLineas] = useState<POSLinea[]>([]);
  const [porcDesc, setPorcDesc] = useState(0);
  const [porcDesc2, setPorcDesc2] = useState(0);
  const [porcDesc3, setPorcDesc3] = useState(0);
  const [formaDePago, setFormaDePago] = useState('');
  const [ordenCompra, setOrdenCompra] = useState('');
  const [transporte, setTransporte] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  /** YYYY-MM-DD para el input de fecha de emisión */
  const [fechaEmision, setFechaEmision] = useState(() => toLocalDateStringYMD());
  const [observaciones, setObservaciones] = useState('');
  const [saving, setSaving] = useState(false);
  const [facturaEmitida, setFacturaEmitida] = useState<any>(null);

  const handleClienteSelect = useCallback((c: any) => {
    const cli = c as POSCliente;
    setCliente(cli);
    if (cli.condVta) {
      const fp = cli.condVta.replace(' ', '.').toUpperCase();
      setFormaDePago(['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'CONTRAREEMBOLSO'].includes(fp) ? fp : cli.condVta);
    }
    if (cli.nroExpreso) setTransporte(String(cli.nroExpreso));
    if (cli.direccionEnvio) setUbicacion(cli.direccionEnvio);
    if (cli.porcDesc) setPorcDesc(cli.porcDesc);
    if (cli.porcDesc2) setPorcDesc2(cli.porcDesc2);
    if (cli.porcDesc3) setPorcDesc3(cli.porcDesc3);
  }, []);

  const handleAgregarProducto = useCallback((item: SuggestionItem) => {
    setLineas((prev) => [
      ...prev,
      {
        id: genId(),
        codigoArticulo: item.codigoArticulo,
        descripcionArticulo: item.descripcion,
        cantidad: 1,
        precioUnitario: item.precioUnitario,
        descuento: 0,
        subtotal: item.precioUnitario,
        um: item.um,
        stockDisponible: item.stockDisponible,
      },
    ]);
  }, []);

  const handleLineaChange = useCallback(
    (id: string, field: keyof POSLinea, value: string | number) => {
      setLineas((prev) =>
        prev.map((l) => {
          if (l.id !== id) return l;
          const updated = { ...l, [field]: value };
          updated.subtotal = calcularSubtotal(updated);
          return updated;
        }),
      );
    },
    [],
  );

  const handleLineaFilled = useCallback((id: string, data: Partial<POSLinea>) => {
    setLineas((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, ...data };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  }, []);

  const handleEliminarLinea = useCallback((id: string) => {
    setLineas((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const handleDuplicarLinea = useCallback((id: string) => {
    setLineas((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const copia: POSLinea = { ...prev[idx], id: genId() };
      const next = [...prev];
      next.splice(idx + 1, 0, copia);
      return next;
    });
  }, []);

  const handleAgregarLineaVacia = useCallback(() => {
    setLineas((prev) => [
      ...prev,
      { id: genId(), codigoArticulo: '', descripcionArticulo: '', cantidad: 1, precioUnitario: 0, descuento: 0, subtotal: 0, um: '', notas: '' },
    ]);
  }, []);

  const handlePorcDescChange = (field: 'porcDesc' | 'porcDesc2' | 'porcDesc3', v: number) => {
    if (field === 'porcDesc') setPorcDesc(v);
    else if (field === 'porcDesc2') setPorcDesc2(v);
    else setPorcDesc3(v);
  };

  // ── Totals ───────────────────────────────────────────────────
  const subtotalNeto = lineas.reduce((acc, l) => acc + l.subtotal, 0);
  const importeDesc1 = (subtotalNeto * porcDesc) / 100;
  const baseDesc2 = subtotalNeto - importeDesc1;
  const importeDesc2 = (baseDesc2 * porcDesc2) / 100;
  const baseDesc3 = baseDesc2 - importeDesc2;
  const importeDesc3 = (baseDesc3 * porcDesc3) / 100;
  const subtotalConDesc = baseDesc3 - importeDesc3;
  const iva = Math.round(subtotalConDesc * IVA_ALICUOTA * 100) / 100;
  const total = subtotalConDesc + iva;

  // ── Guardar ──────────────────────────────────────────────────
  const handleGuardar = async (emitir: boolean) => {
    if (!cliente) { enqueueSnackbar('Seleccioná un cliente', { variant: 'warning' }); return; }
    if (nroSuc <= 0) { enqueueSnackbar('Seleccioná una sucursal', { variant: 'warning' }); return; }
    const lineasValidas = lineas.filter((l) => l.codigoArticulo.trim() && l.cantidad > 0);
    if (lineasValidas.length === 0) { enqueueSnackbar('Agregá al menos un artículo con cantidad', { variant: 'warning' }); return; }
    if (!fechaEmision?.trim()) { enqueueSnackbar('Ingresá la fecha de emisión', { variant: 'warning' }); return; }

    setSaving(true);
    try {
      const detalles = lineasValidas.map((l, idx) => ({
        nroIdformulario: 0,
        nroItem: idx + 1,
        codigoArticulo: l.codigoArticulo,
        descripcionArticulo: l.descripcionArticulo,
        cantidad: l.cantidad,
        precioUnitario: l.precioUnitario,
        descuento: l.descuento,
        porcDesc: l.descuento,
        subtotal: l.subtotal,
        um: l.um,
        notas: l.notas,
      }));

      const payload: any = {
        nroFormulario: 0,
        nroTipoFormulario: '12',
        nroCliente: cliente.nroCliente,
        nombre: cliente.nombre,
        nroLista: obtenerCodigoListaCliente(cliente),
        nroVendedor: cliente.nroVendedor,
        detalles,
        porcDesc,
        porcDesc2,
        porcDesc3,
        ordenCompra,
        transporte,
        ubicacion,
        formaDePago,
        fechaEmision: parseLocalDate(fechaEmision.trim()),
        nroSuc,
        idCompania,
        nroFormularioPedido: nroPedidoOrigen ?? undefined,
        descripcion: observaciones.trim(),
      };

      const result = await facturaVentaService.createFacturaVenta(payload);
      setFacturaEmitida(result);
      enqueueSnackbar(emitir ? 'Factura emitida correctamente' : 'Borrador guardado', { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(extractApiError(e), { variant: 'error', style: { whiteSpace: 'pre-wrap' } });
    } finally {
      setSaving(false);
    }
  };

  const nroLista = obtenerCodigoListaCliente(cliente) || undefined;

  const handleNuevaFactura = () => {
    setFacturaEmitida(null);
    if (modoVerPos || modoDesdePedido) {
      navigate('/ventas/facturas/pos', { replace: true });
      return;
    }
    setCliente(null);
    setLineas([]);
    setPorcDesc(0); setPorcDesc2(0); setPorcDesc3(0);
    setFormaDePago(''); setOrdenCompra('');
    setTransporte(''); setUbicacion('');
    setFechaEmision(toLocalDateStringYMD());
    setObservaciones('');
  };

  if ((modoVerPos && loadingFactura) || (modoDesdePedido && loadingPedido)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {modoVerPos ? 'Cargando factura…' : 'Cargando pedido…'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1, sm: 2 } }}>
      {/* ── Header ── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} mb={2.5} flexWrap="wrap">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/ventas/facturas')} size="small" sx={{ textTransform: 'none' }}>
          Facturas
        </Button>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Typography variant="h5" fontWeight={700} color="primary.main">
          {modoVerPos ? tituloPos || `Factura POS #${nroVer}` : modoDesdePedido ? `Facturar pedido #${pedidoIdNum}` : 'Nueva Factura POS'}
        </Typography>
        <Chip label="BETA" size="small" color="warning" variant="outlined" />

        {!loadingSuc && companias.length > 1 && (
          <Box sx={{ ml: { sm: 'auto' } }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Compañía</InputLabel>
              <Select
                value={idCompania}
                label="Compañía"
                onChange={(e) => setIdCompania(e.target.value)}
              >
                {companias.map((c) => (
                  <MenuItem key={c.idCompania} value={c.idCompania}>
                    {c.nombre || c.idCompania}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Stack>

      {/* ── Layout split ── */}
      <Grid container spacing={2} alignItems="flex-start">
        {/* Columna izquierda ~65% */}
        <Grid item xs={12} md={8}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>Cliente</Typography>
              <FacturaPOSClientePanel
                cliente={cliente}
                onClienteSelect={handleClienteSelect}
                readOnly={modoVerPos}
              />
            </Box>
            {!modoVerPos && (
              <Box>
                <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>Agregar artículo</Typography>
                <FacturaPOSSearchBar nroLista={nroLista} onAgregarProducto={handleAgregarProducto} />
              </Box>
            )}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
                <Typography variant="overline" color="text.secondary">Artículos</Typography>
                {lineas.length > 0 && (
                  <Chip label={`${lineas.length} renglón${lineas.length > 1 ? 'es' : ''}`} size="small" />
                )}
              </Stack>
              <FacturaPOSLineasGrid
                lineas={lineas}
                nroLista={nroLista}
                readOnly={modoVerPos}
                onLineaChange={handleLineaChange}
                onLineaFilled={handleLineaFilled}
                onEliminarLinea={handleEliminarLinea}
                onDuplicarLinea={handleDuplicarLinea}
                onAgregarLineaVacia={handleAgregarLineaVacia}
              />
            </Box>
          </Stack>
        </Grid>

        {/* Panel lateral ~35% */}
        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
            <FacturaPOSTotalesPanel
              subtotalNeto={subtotalNeto}
              porcDesc={porcDesc} porcDesc2={porcDesc2} porcDesc3={porcDesc3}
              onPorcDescChange={handlePorcDescChange}
              iva={iva} total={total}
              observaciones={observaciones}
              onObservacionesChange={setObservaciones}
              cliente={cliente}
              fechaEmision={fechaEmision}
              onFechaEmisionChange={setFechaEmision}
              nroVendedor={cliente?.nroVendedor ?? 0}
              nombreVendedorFallback={cliente?.vendedorInfo?.nombreVendedor ?? undefined}
              onNroVendedorChange={(nro, nombre) => {
                setCliente((prev) => {
                  if (!prev) return null;
                  if (!nro) return { ...prev, nroVendedor: undefined, vendedorInfo: undefined };
                  return {
                    ...prev,
                    nroVendedor: nro,
                    vendedorInfo: { nroVendedor: nro, nombreVendedor: nombre },
                  };
                });
              }}
              formaDePago={formaDePago} onFormaDePagoChange={setFormaDePago}
              ordenCompra={ordenCompra} onOrdenCompraChange={setOrdenCompra}
              transporte={transporte} onTransporteChange={setTransporte}
              ubicacion={ubicacion} onUbicacionChange={setUbicacion}
              saving={saving} onGuardar={handleGuardar} cantLineas={lineas.length}
              sucursales={sucursales}
              nroSuc={nroSuc}
              onNroSucChange={setNroSuc}
              loadingSucursales={loadingSuc}
              readOnly={modoVerPos}
            />
          </Box>
        </Grid>
      </Grid>

      {/* ── Dialog resultado ── */}
      <Dialog open={Boolean(facturaEmitida)} onClose={() => setFacturaEmitida(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{facturaEmitida?.caeNro ? 'Factura emitida' : 'Borrador guardado'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} mt={1}>
            {facturaEmitida?.nroComprobante && (
              <Typography>
                Comprobante N° {facturaEmitida.letra ?? ''} {facturaEmitida.nroSuc ?? ''}-{facturaEmitida.nroComprobante}
              </Typography>
            )}
            {facturaEmitida?.caeNro && (
              <>
                <Typography variant="body2" color="text.secondary">CAE: <strong>{facturaEmitida.caeNro}</strong></Typography>
                {facturaEmitida.caeVencimiento && (
                  <Typography variant="body2" color="text.secondary">Vencimiento: {facturaEmitida.caeVencimiento}</Typography>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/ventas/facturas')}>Ver listado</Button>
          <Button variant="contained" onClick={handleNuevaFactura}>Nueva factura</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NuevaFacturaPOS;
